import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import {
  MaskedLines, Reveal, SectionHead, TempImage, EditorialMarquee,
  PlaceholderTag, StatBlock, StoryCard,
} from "../components/common";
import {
  ORG, IMAGES, WORK_AREAS, IMPACT_STATS, STATS_DISCLAIMER, IMPACT_STORIES,
  FEATURED_STORY, INVOLVE_WAYS, MARQUEE_ITEMS,
} from "../data/site";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-charcoal">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.15, opacity: 0.5 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          src={IMAGES.hero.url}
          alt={IMAGES.hero.alt}
          className="w-full h-[115%] object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-charcoal/40" />
      {!IMAGES.hero.real && (
        <div className="absolute top-24 right-6 md:right-12 z-20">
          <PlaceholderTag dark text="Temporary placeholder image" />
        </div>
      )}
      <div className="relative z-10 w-full max-w-[90rem] mx-auto px-6 md:px-12 pb-32 pt-48">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-xs font-bold uppercase tracking-[0.25em] text-white/70"
        >
          {ORG.name} — Reg. No. {ORG.registration} · Ahmedabad, Gujarat
        </motion.p>
        <MaskedLines
          lines={["Creating Change.", "Building Hope."]}
          className="mt-6 font-serif text-warmwhite font-light tracking-tighter leading-[1.05] text-5xl sm:text-7xl lg:text-8xl"
        />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-white/85">
            Susanna Charitable Trust works to create meaningful and lasting change in the communities we serve.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/donate"
              data-testid="hero-donate-cta"
              className="rounded-full bg-terracotta text-white px-8 py-4 text-sm font-semibold hover:bg-terracotta-dark hover:-translate-y-1 transition-all duration-300"
            >
              Support Our Work
            </Link>
            <Link
              to="/our-work"
              data-testid="hero-explore-cta"
              className="rounded-full border border-white/40 text-white px-8 py-4 text-sm font-semibold hover:bg-white/10 hover:border-white transition-all duration-300"
            >
              Explore Our Work
            </Link>
          </div>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/60">Scroll</span>
        <motion.span
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-10 bg-white/50 origin-top"
        />
      </motion.div>
    </section>
  );
}

function Intro() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-[90rem] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <SectionHead overline="Who We Are" title="A Commitment to Meaningful Change" />
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <Reveal delay={0.15}>
            <p className="text-base sm:text-lg leading-relaxed text-charcoal/80">
              Founded on {ORG.founded}, Susanna Charitable Trust is a registered charitable trust
              (Reg. No. {ORG.registration}) based in Ahmedabad, Gujarat. The Trust's stated objective is to help
              underprivileged children with their education and to support women's empowerment.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-charcoal/60">
              Since 2017, the Trust's notebook drives, food kit distributions, flood relief and support for
              widows have reached students and families in more than sixty locations across Gujarat — work
              documented openly on this website, year after year.
            </p>
            <Link
              to="/about"
              data-testid="intro-learn-more"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-charcoal border-b border-charcoal pb-1 hover:text-terracotta hover:border-terracotta transition-colors"
            >
              Learn More <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WorkPreview() {
  return (
    <section className="py-24 md:py-32 bg-softgray">
      <div className="max-w-[90rem] mx-auto px-6 md:px-12">
        <SectionHead
          overline="What We Do"
          title="Our Areas of Work"
          copy="Four programme areas, each documented with real events, photographs and locations — explore the full archive on the Our Work page."
        />
        <div className="mt-16 md:mt-20">
          {WORK_AREAS.map((area, i) => (
            <Reveal key={area.slug} delay={i * 0.05}>
              <Link
                to="/our-work"
                data-testid={`work-area-${area.slug}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 items-center border-t border-charcoal/10 py-10 md:py-12 hover:bg-white/60 transition-colors duration-500 px-2 md:px-4 -mx-2 md:-mx-4 rounded-xl"
              >
                <span className="md:col-span-1 font-mono text-sm text-charcoal/40">{area.number}</span>
                <h3 className="md:col-span-4 font-serif text-3xl md:text-4xl font-light tracking-tight text-charcoal group-hover:text-terracotta transition-colors duration-300">
                  {area.title}
                </h3>
                <div className="md:col-span-2 order-3 md:order-none overflow-hidden rounded-xl md:rounded-lg bg-softgray aspect-[16/9] md:aspect-auto md:w-24 md:h-16">
                  <img src={area.image.url} alt={area.image.alt} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                </div>
                <p className="md:col-span-4 order-4 md:order-none text-sm leading-relaxed text-charcoal/60">{area.description}</p>
                <ArrowUpRight className="hidden md:block md:col-span-1 justify-self-end self-center h-5 w-5 text-charcoal/40 group-hover:text-terracotta group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactPreview() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-[90rem] mx-auto px-6 md:px-12">
        <SectionHead
          overline="Evidence of Impact"
          title="Impact, Measured Honestly"
          copy="Every figure below is compiled from the Trust's own documented event reports — nothing is estimated."
        />
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {IMPACT_STATS.map((s, i) => (
            <StatBlock key={s.label} label={s.label} value={s.value} note={s.note} index={i} />
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl text-xs leading-relaxed text-charcoal/45">{STATS_DISCLAIMER}</p>
        </Reveal>
        <div className="mt-24 flex items-end justify-between gap-6 flex-wrap">
          <h3 className="font-serif text-3xl md:text-4xl font-light tracking-tight">Stories of Impact</h3>
          <Link to="/impact" data-testid="impact-view-all" className="inline-flex items-center gap-2 text-sm font-semibold text-charcoal border-b border-charcoal pb-1 hover:text-terracotta hover:border-terracotta transition-colors">
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {IMPACT_STORIES.map((s, i) => (
            <StoryCard key={i} story={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedStory() {
  const f = FEATURED_STORY;
  return (
    <section className="py-24 md:py-32 bg-charcoal text-warmwhite">
      <div className="max-w-[90rem] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
        <Reveal>
          <TempImage src={f.image.url} alt={f.image.alt} ratio="aspect-[4/5]" dark real={f.real} />
        </Reveal>
        <div>
          <SectionHead dark overline="Featured Story" title={f.heading} />
          <Reveal delay={0.2}>
            <dl className="mt-10 space-y-5 text-sm">
              {[["Project", f.projectName], ["Location", f.location], ["Date", f.date]].map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-4 border-b border-white/10 pb-4">
                  <dt className="w-24 shrink-0 text-xs font-mono uppercase tracking-widest text-white/50">{k}</dt>
                  <dd className="text-white/85">{v ?? <PlaceholderTag dark />}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-8 text-base leading-relaxed text-white/70">
              {f.story ?? "A full documentary feature — with photography, voices from the community and measured outcomes — will be published here once an official project story is supplied by the Trust."}
            </p>
            {f.impact && (
              <p className="mt-4 text-sm italic font-serif text-white/50">{f.impact}</p>
            )}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/our-work"
                data-testid="featured-story-read-more"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-3.5 text-sm font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Read More <ArrowRight className="h-4 w-4" />
              </Link>
              {f.facebookUrl && (
                <a
                  href={f.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="featured-story-facebook-link"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors"
                >
                  Original post <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function InvolvePreview() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-[90rem] mx-auto px-6 md:px-12">
        <SectionHead overline="Participate" title="How You Can Help" />
        <div className="mt-14">
          {INVOLVE_WAYS.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.05}>
              <Link
                to={w.to}
                {...(w.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={`involve-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex items-baseline gap-6 md:gap-10 border-t border-charcoal/10 py-8 md:py-10 hover:bg-softgray/70 transition-colors duration-500 px-2 md:px-4 -mx-2 md:-mx-4 rounded-xl"
              >
                <span className="font-mono text-sm text-charcoal/40">{w.number}</span>
                <h3 className="font-serif text-2xl md:text-4xl font-light tracking-tight text-charcoal group-hover:text-terracotta transition-colors duration-300 w-48 md:w-72 shrink-0">
                  {w.title}
                </h3>
                <p className="hidden md:block text-sm text-charcoal/60 grow">{w.description}</p>
                <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-charcoal/40 group-hover:text-terracotta group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-20 bg-softgray rounded-3xl px-8 py-14 md:p-20 text-center">
            <h3 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-charcoal">
              Your support makes this work possible.
            </h3>
            <p className="mt-5 max-w-xl mx-auto text-sm md:text-base text-charcoal/60">
              Contributions of every size help the Trust plan and sustain its programmes.
            </p>
            <Link
              to="/donate"
              data-testid="home-bottom-donate-cta"
              className="mt-8 inline-flex rounded-full bg-terracotta text-white px-10 py-4 text-sm font-semibold hover:bg-terracotta-dark hover:-translate-y-1 transition-all duration-300"
            >
              Support Our Work
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Intro />
      <EditorialMarquee items={MARQUEE_ITEMS} />
      <WorkPreview />
      <ImpactPreview />
      <FeaturedStory />
      <InvolvePreview />
    </>
  );
}
